import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, Tag, ArrowLeft, Eye } from 'lucide-react';
import { getBlogPostBySlug, getMachines } from '../services/apiService';
import YouTubeEmbed from '../components/YouTubeEmbed';

const BlogDetail = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [relatedMachines, setRelatedMachines] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPost();
  }, [slug]);

  const fetchPost = async () => {
    try {
      setLoading(true);
      const postData = await getBlogPostBySlug(slug);
      setPost(postData);

      // Fetch related machines if any
      if (postData.relatedMachines && postData.relatedMachines.length > 0) {
        const allMachines = await getMachines();
        const related = allMachines.filter(m => 
          postData.relatedMachines.includes(m.id)
        );
        setRelatedMachines(related);
      }
    } catch (error) {
      console.error('Error fetching blog post:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2" style={{ borderColor: '#5C3A21' }}></div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4" style={{ color: '#5C3A21' }}>Article non trouvé</h1>
          <Link to="/blog" className="text-blue-600 hover:underline">Retour au blog</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-16" style={{ backgroundColor: '#F5EBDD' }}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <Link
          to="/blog"
          className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-8 transition-colors"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Retour au blog
        </Link>

        {/* Article Content */}
        <article className="bg-white rounded-xl shadow-lg overflow-hidden">
          {/* Featured Image */}
          <div className="w-full h-96 overflow-hidden">
            <img
              src={post.featuredImage}
              alt={post.title}
              className="w-full h-full object-cover"
            />
          </div>

          <div className="p-8 md:p-12">
            {/* Tags */}
            {post.tags && post.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4">
                {post.tags.map(tag => (
                  <span
                    key={tag}
                    className="px-3 py-1 text-sm rounded-full bg-amber-100 text-amber-800 flex items-center"
                  >
                    <Tag className="w-3 h-3 mr-1" />
                    {tag}
                  </span>
                ))}
              </div>
            )}

            {/* Title */}
            <h1 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: '#5C3A21' }}>
              {post.title}
            </h1>

            {/* Meta Info */}
            <div className="flex items-center space-x-6 text-gray-600 mb-8 pb-6 border-b">
              <div className="flex items-center space-x-2">
                <Calendar className="w-5 h-5" />
                <span>{formatDate(post.createdAt)}</span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="font-semibold">Par {post.author}</span>
              </div>
              {post.views > 0 && (
                <div className="flex items-center space-x-2">
                  <Eye className="w-5 h-5" />
                  <span>{post.views} vues</span>
                </div>
              )}
            </div>

            {/* Content */}
            <div className="prose prose-lg max-w-none mb-8">
              <div className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                {post.content}
              </div>
            </div>


            {/* YouTube Video */}
            {post.youtubeUrl && (
              <div className="my-8">
                <YouTubeEmbed url={post.youtubeUrl} title={post.title} />
              </div>
            )}


            {/* Additional Images */}
            {post.images && post.images.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                {post.images.map((img, idx) => (
                  <img
                    key={idx}
                    src={img}
                    alt={`${post.title} - Image ${idx + 1}`}
                    className="w-full h-64 object-cover rounded-lg"
                  />
                ))}
              </div>
            )}
          </div>
        </article>

        {/* Related Machines */}
        {relatedMachines.length > 0 && (
          <div className="mt-12">
            <h2 className="text-2xl font-bold mb-6" style={{ color: '#5C3A21' }}>
              Machines recommandées
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {relatedMachines.map(machine => (
                <Link
                  key={machine.id}
                  to={`/machines/${machine.slug}`}
                  className="bg-white rounded-lg p-6 shadow-md hover:shadow-xl transition-shadow"
                >
                  <div className="flex items-start space-x-4">
                    <img
                      src={machine.image}
                      alt={machine.name}
                      className="w-24 h-24 object-cover rounded-lg"
                    />
                    <div>
                      <h3 className="font-bold text-lg mb-2" style={{ color: '#5C3A21' }}>
                        {machine.name}
                      </h3>
                      <p className="text-sm text-gray-600 mb-2">{machine.description}</p>
                      <p className="font-semibold" style={{ color: '#5C3A21' }}>
                        {machine.price}
                      </p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogDetail;
